import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/shared/utils/utils";
import { useBranding } from "@/shared/contexts/BrandingContext";

/**
 * Brand-aware Button Component
 *
 * Automatically adapts colors based on active brand:
 * - Aegis360: Blue color scheme
 * - Greenshield: Emerald color scheme
 *
 * Status variants (success, error, warning, info) remain semantic.
 *
 * Accessibility features:
 * - Visible focus rings (WCAG 2.1 AA compliant)
 * - aria-busy and aria-disabled states
 * - Minimum 44px touch targets
 * - Loading state feedback
 */

const buttonVariants = cva(
  // Base classes - consistent across all variants
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold ring-offset-background transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed relative overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-button interactive-button",
  {
    variants: {
      variant: {
        // Note: Brand colors applied dynamically via useBranding hook
        // These are fallback classes for static contexts
        default:
          "bg-primary text-white shadow-md hover:bg-primary/90 hover:shadow-lg active:scale-[0.97] disabled:opacity-50",
        destructive:
          "bg-red-500 text-white shadow-md hover:bg-red-600 hover:shadow-lg focus-visible:ring-red-500 active:bg-red-700 disabled:bg-red-300",
        outline:
          "border-2 border-primary/20 bg-background text-primary shadow-sm hover:bg-primary/5 hover:border-primary/30 hover:shadow-md active:bg-primary/10 disabled:border-gray-200 disabled:text-gray-400",
        secondary:
          "bg-primary/10 text-primary shadow-sm hover:bg-primary/20 hover:shadow-md active:bg-primary/30 disabled:bg-gray-100 disabled:text-gray-400",
        ghost:
          "text-primary hover:bg-primary/10 hover:shadow-sm active:bg-primary/20 disabled:text-gray-400 disabled:hover:bg-transparent",
        link: "text-primary underline-offset-4 hover:underline active:text-primary/80 disabled:text-gray-400 disabled:no-underline",
        // Semantic status colors - remain consistent across brands
        success:
          "bg-green-500 text-white shadow-md hover:bg-green-600 hover:shadow-lg focus-visible:ring-green-500 active:bg-green-700 disabled:bg-green-300",
        warning:
          "bg-amber-500 text-white shadow-md hover:bg-amber-600 hover:shadow-lg focus-visible:ring-amber-500 active:bg-amber-700 disabled:bg-amber-300",
        info: "bg-blue-500 text-white shadow-md hover:bg-blue-600 hover:shadow-lg focus-visible:ring-blue-500 active:bg-blue-700 disabled:bg-blue-300",
      },
      size: {
        // Removed xs (text-xs = 12px) for accessibility
        sm: "h-9 px-4 text-sm rounded-lg min-w-[44px]", // Touch-friendly minimum
        default: "h-11 px-6 py-3 text-sm min-w-[44px]",
        lg: "h-12 px-8 text-base rounded-lg min-w-[44px]",
        xl: "h-14 px-10 text-lg rounded-xl min-w-[44px]",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]", // Touch target compliant
        "icon-sm": "h-10 w-10 min-h-[44px] min-w-[44px]",
        "icon-lg": "h-12 w-12 min-h-[44px] min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const { isAegis360 } = useBranding();
    const Comp = asChild ? Slot : "button";

    const isDisabled = disabled || loading;

    // Apply brand-specific colors dynamically
    // Only for brand-contextual variants (default, outline, secondary, ghost, link)
    const brandAwareClassName = React.useMemo(() => {
      if (
        !variant ||
        ["success", "warning", "info", "destructive"].includes(variant)
      ) {
        // Status variants keep their semantic colors
        return "";
      }

      // Apply brand colors based on context
      const brandColor = isAegis360 ? "blue" : "emerald";
      const brandClasses = {
        default: `bg-${brandColor}-600 hover:bg-${brandColor}-700 focus-visible:ring-${brandColor}-500 active:bg-${brandColor}-800`,
        outline: `border-${brandColor}-200 text-${brandColor}-700 hover:bg-${brandColor}-50 hover:border-${brandColor}-300 focus-visible:ring-${brandColor}-500 active:bg-${brandColor}-100`,
        secondary: `bg-${brandColor}-100 text-${brandColor}-800 hover:bg-${brandColor}-200 focus-visible:ring-${brandColor}-500 active:bg-${brandColor}-300`,
        ghost: `text-${brandColor}-700 hover:bg-${brandColor}-50 focus-visible:ring-${brandColor}-500 active:bg-${brandColor}-100`,
        link: `text-${brandColor}-600 hover:text-${brandColor}-700 focus-visible:ring-${brandColor}-500 active:text-${brandColor}-800`,
      };

      return brandClasses[variant as keyof typeof brandClasses] || "";
    }, [variant, isAegis360]);

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          brandAwareClassName,
          className,
        )}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        aria-disabled={isDisabled}
        aria-label={loading && loadingText ? loadingText : props["aria-label"]}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" aria-hidden="true" />
            {loadingText || children}
          </>
        ) : (
          <>
            {leftIcon && <span aria-hidden="true">{leftIcon}</span>}
            {children}
            {rightIcon && <span aria-hidden="true">{rightIcon}</span>}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
